import React, { useState } from 'react'
import { Table, Pagination, SelectPicker } from 'rsuite'
import { formatDateToReadable } from '../../../utils/helper-functions'
import { ReactComponent as Success } from '../../../assets/svgs/success.svg'
import { ReactComponent as Warning } from '../../../assets/svgs/warning.svg'
import { ReactComponent as Error } from '../../../assets/svgs/error.svg'
import { t } from '../../../utils/i18n'
import Legends from '../legends/legends'
import EditableCell from '../editable-cell/editable-cell'
import './table.scss'
import { RowDataType } from 'rsuite/esm/Table'
import { InfoTooltip } from '../../atoms/info-tooltip/info-tooltip'
import { getFailedSyncMsg, getNextSyncDate } from '../../../containers/utils'
import { ReactComponent as Info } from '../../../assets/svgs/Info.svg'

const { Column, HeaderCell, Cell } = Table

export type TableColumn = {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  width?: number
  fixed?: boolean
  isRowSpan?: boolean
  sortable?: boolean
  clickable?: boolean
  isEditable?: boolean
  isRequired?: boolean
  flexGrow?: number
  toolTipContent?: string
}

export type CustomTableProps<T> = {
  data: T[]
  columns?: TableColumn[]
  isLoading?: boolean
  isPaginated?: boolean
  hasLegends?: boolean
  currentPage?: number
  recordsPerPage?: number
  pageRange?: string
  onPageChange?: (p: number) => void
  onLimitChange?: (l: number) => void
  total?: number
  rowHeight?: number
  isVirtualize?: boolean
  tableHeight?: number
  autoHeight?: boolean
  // eslint-disable-next-line
  columnSorting?: (cur?: any) => void
  handleEditableChange?: (uniqueId: string, dataKey: string, newValue: string) => void
  onCellClicked?: (key: string, data: RowDataType) => void
}

function inferColumns<T>(data: T[]): TableColumn[] {
  if (data.length === 0) return []
  return Object.keys(data).map((key) => ({
    key,
    label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()),
    width: 150,
  }))
}

export function CustomTable<T extends object>({
  data,
  columns,
  isLoading,
  isPaginated = false,
  hasLegends = false,
  currentPage = 1,
  recordsPerPage = 5,
  pageRange,
  onPageChange,
  onLimitChange,
  total,
  rowHeight = 40,
  isVirtualize = false,
  tableHeight,
  autoHeight = true,
  columnSorting,
  handleEditableChange,
  onCellClicked,
}: CustomTableProps<T>) {
  const [loading, setLoading] = useState(false)

  const limitOptions: number[] = (pageRange || '')
    ?.split(',')
    .map((val) => parseInt(val.trim(), 10))
    .filter((val) => !isNaN(val))

  const loadMore = async () => {
    if (loading || recordsPerPage >= Number(total)) return

    setLoading(true)

    const newLimit = Math.min(recordsPerPage + recordsPerPage, Number(total))
    onLimitChange?.(newLimit)

    setLoading(false)
  }

  const limitPickerData = limitOptions?.map((val) => ({
    label: t('page.picker').replace('{{val}}', val),
    value: val,
  }))

  const derivedColumns = columns ?? inferColumns(data)

  const legends = [
    {
      label: t('sync.success'),
      icon: <Success />,
    },
    {
      label: t('sync.warning'),
      icon: <Warning />,
    },
    {
      label: t('sync.error'),
      icon: <Error />,
    },
  ]

  const handleScroll = (x: number, y: number) => {
    if (isVirtualize) {
      const contextHeight = data.length * rowHeight
      const top = Math.abs(y)
      if (
        !loading &&
        contextHeight - top - Number(tableHeight) < 300 &&
        data.length < Number(total)
      ) {
        loadMore()
      }
    }
  }
  const handleSortColumn = (sortColumn: string, sortType?: string) => {
    columnSorting?.(sortType)
  }

  return (
    <div className="custom-table">
      <Table
        data={data}
        loading={isLoading}
        autoHeight={autoHeight}
        height={tableHeight}
        rowHeight={rowHeight}
        onScroll={handleScroll}
        defaultSortType="asc"
        onSortColumn={(sortColumn, sortType) => {
          handleSortColumn(sortColumn, sortType)
        }}
      >
        {derivedColumns.map(
          ({
            key,
            label,
            fixed,
            align,
            width,
            isRowSpan,
            clickable,
            isEditable,
            sortable,
            isRequired,
            toolTipContent,
            ...rest
          }) => (
            <Column
              {...rest}
              key={key}
              fixed={fixed}
              width={width}
              align={align}
              fullText
              sortable={sortable}
              rowSpan={
                // eslint-disable-next-line
                isRowSpan ? (rowData: any) => rowData[`${key}__rowSpan`] ?? undefined : undefined
              }
            >
              <HeaderCell className="t-header form-label">
                {toolTipContent ? (
                  <InfoTooltip
                    styleClass="token-health-header"
                    tooltipContent={toolTipContent}
                    placement={'left'}
                    followCursor={true}
                    delay={300}
                  >
                    {
                      <>
                        {label} <Info className="info-icon-color" />
                      </>
                    }
                  </InfoTooltip>
                ) : (
                  label
                )}
              </HeaderCell>

              <Cell className="t-data">
                {
                  // eslint-disable-next-line
                  (rowData: any) => {
                    let value
                    const span = isRowSpan ? rowData[`${key}__rowSpan`] : undefined
                    // If it's part of a spanned group and not the first row, hide it
                    if (isRowSpan && span === 0) {
                      return <div style={{ display: 'none' }} />
                    }
                    if (rowData[key] !== undefined && key !== 'tokenStatus') {
                      value = rowData[key]
                    } else if (
                      rowData.metaInfo &&
                      Object.prototype.hasOwnProperty.call(rowData.metaInfo, key)
                    ) {
                      const status = rowData.metaInfo[key]?.status?.toLowerCase()
                      value =
                        status === 'success' ? (
                          <InfoTooltip
                            styleClass="input-info"
                            tooltipContent={t('meta.info.nextSync').replace(
                              '{{nextSyncAt}}',
                              getNextSyncDate(rowData.metaInfo[key])
                            )}
                            placement={'right'}
                            followCursor={true}
                            delay={300}
                          >
                            <Success style={{ width: '1.3rem', height: '1.3rem' }} />
                          </InfoTooltip>
                        ) : status === 'failed' ? (
                          <InfoTooltip
                            styleClass="input-info"
                            tooltipContent={getFailedSyncMsg(rowData.metaInfo[key])}
                            placement={'left'}
                            followCursor={true}
                            delay={300}
                          >
                            <Error style={{ width: '1.3rem', height: '1.3rem' }} />
                          </InfoTooltip>
                        ) : status === 'in_progress' ? (
                          <Warning style={{ width: '1.3rem', height: '1.3rem' }} />
                        ) : (
                          '-'
                        )
                    } else if (key === 'tokenStatus') {
                      const tokenStatus = rowData[key]?.toLowerCase()
                      value =
                        tokenStatus === 'healthy' ? (
                          <InfoTooltip
                            styleClass="input-info"
                            tooltipContent={t('token.healthy.hover')}
                            placement={'right'}
                            followCursor={true}
                            delay={300}
                          >
                            <div className={`status-cell-bm mapped`}>{rowData[key]}</div>
                          </InfoTooltip>
                        ) : tokenStatus === 'error' ? (
                          <InfoTooltip
                            styleClass="input-info"
                            tooltipContent={rowData?.errorHoverText}
                            placement={'right'}
                            followCursor={true}
                            delay={300}
                          >
                            <div className={`status-cell-bm unmapped`}>{rowData[key]}</div>
                          </InfoTooltip>
                        ) : (
                          <>-</>
                        )
                    } else {
                      value =
                        key === '' ? (
                          <a
                            className={`${rowData['status']?.toLowerCase() === 'mapped' ? 'enable-cta' : 'disable-cta'}`}
                            href={`/business-accounts/${rowData['businessId']}`}
                          >
                            View →
                          </a>
                        ) : (
                          <InfoTooltip
                            styleClass="input-info"
                            tooltipContent={t('meta.info.noSync')}
                            placement={'right'}
                            followCursor={true}
                            delay={300}
                          >
                            -
                          </InfoTooltip>
                        )
                    }

                    if (key === 'status') {
                      let statusClr = ''

                      if (typeof value === 'string' && value?.toLowerCase() === 'mapped') {
                        statusClr = 'mapped'
                      } else if (typeof value === 'string' && value?.toLowerCase() === 'unmapped') {
                        statusClr = 'unmapped'
                      } else if (
                        typeof value === 'string' &&
                        value?.toLowerCase() === 'inprogress'
                      ) {
                        statusClr = 'inprogress'
                      }
                      return <div className={`status-cell-bm ${statusClr}`}>{value}</div>
                    }

                    if (key.toLowerCase().includes('date')) {
                      return formatDateToReadable(value)
                    }

                    if (clickable && typeof value === 'string' && key === 'businessAccountId') {
                      return <a href={`/business-accounts/${rowData[key]}`}>{value}</a>
                    }
                    if (clickable && typeof value === 'string' && key === 'cta') {
                      return (
                        <a
                          onClick={() => onCellClicked?.(key, rowData)}
                          style={{ cursor: 'pointer' }}
                        >
                          {value}
                        </a>
                      )
                    }

                    if (isEditable && typeof value === 'string') {
                      return (
                        <EditableCell
                          required={isRequired}
                          key={rowData.recordSystemId}
                          rowData={rowData}
                          dataKey={key}
                          onChange={(uniqueId: string, dataKey: string, newValue: string) => {
                            handleEditableChange?.(uniqueId, dataKey, newValue)
                          }}
                        />
                      )
                    }

                    return value
                  }
                }
              </Cell>
            </Column>
          )
        )}
      </Table>
      {isPaginated ? (
        <div className="pagination-container">
          <span className="body-sm">{t('rows.per.page')}</span>
          <SelectPicker
            className="page-select"
            data={limitPickerData}
            value={recordsPerPage}
            cleanable={false}
            searchable={false}
            size="xs"
            style={{ width: 150 }}
            onChange={(value: number | null) => onLimitChange?.(Number(value))}
          />
          <Pagination
            className="pagination"
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['|', 'pager']}
            total={Number(total)}
            limit={recordsPerPage}
            activePage={currentPage}
            onChangePage={onPageChange}
            onChangeLimit={onLimitChange}
          />
        </div>
      ) : null}

      {hasLegends && (
        <>
          <hr className="divider" />
          <section className="legends-section">
            {legends.map((legend, i) => (
              <Legends label={legend.label} icon={legend.icon} key={i} />
            ))}
          </section>
        </>
      )}
    </div>
  )
}

export default CustomTable
